import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, FileText, SlidersHorizontal, History, Zap, Globe } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

const FEATURES = [
  { icon: Zap, title: "Qualità madrelingua", desc: "Traduzioni che colgono sfumature, metafore e modi di dire — non parola per parola." },
  { icon: FileText, title: "Traduzione documenti", desc: "Carica PDF, Word o TXT e ricevi il documento tradotto pronto da scaricare." },
  { icon: SlidersHorizontal, title: "Tono di voce", desc: "Formale, tecnico, copywriter o accademico: guida l'IA verso il registro giusto." },
  { icon: History, title: "Cronologia", desc: "Ritrova tutte le tue traduzioni salvate nel tuo profilo, in ogni momento." },
];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-fade pointer-events-none" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 h-72 w-[40rem] rounded-full bg-brand-blue/10 blur-[100px] pointer-events-none" />
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 pt-24 pb-20 md:pt-32 md:pb-28 relative">
          <div className="max-w-3xl">
            <motion.div variants={fade} initial="hidden" animate="show" custom={0}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card/50 px-4 py-1.5 text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-8">
              <Sparkles className="h-3.5 w-3.5 text-brand-pink" /> Powered by Gemini Flash
            </motion.div>
            <motion.h1 variants={fade} initial="hidden" animate="show" custom={1}
              className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light tracking-tighter leading-[1.05]">
              Traduzioni che sembrano scritte da un{" "}
              <span className="text-brand-blue">madrelingua</span>, non da una{" "}
              <span className="text-brand-pink">macchina</span>.
            </motion.h1>
            <motion.p variants={fade} initial="hidden" animate="show" custom={2}
              className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Freed Words usa le IA più avanzate sul mercato per andare oltre il traduttore automatico:
              tono, contesto e stile perfetti, ogni volta.
            </motion.p>
            <motion.div variants={fade} initial="hidden" animate="show" custom={3}
              className="mt-10 flex flex-wrap items-center gap-4">
              <Button data-testid="hero-cta" onClick={() => nav(user ? "/app" : "/register")}
                className="rounded-full bg-brand-blue hover:bg-brand-blue/90 text-white h-12 px-7 text-base gap-2 group">
                {user ? "Vai alla traduzione" : "Inizia gratis"}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button variant="outline" data-testid="hero-pricing" onClick={() => nav("/pricing")}
                className="rounded-full h-12 px-7 text-base border-border">
                Vedi i piani
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-4 md:px-8 pb-28">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="rounded-2xl border border-border bg-card p-6 hover:-translate-y-1 hover:border-brand-blue/40 transition-all duration-300">
              <div className="h-11 w-11 rounded-xl bg-secondary flex items-center justify-center mb-5">
                <f.icon className="h-5 w-5 text-brand-blue" />
              </div>
              <h3 className="font-heading text-lg font-medium mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-4 md:px-8 pb-32">
        <div className="relative rounded-3xl overflow-hidden border border-border p-10 md:p-16 bg-card">
          <div className="absolute -top-32 -right-16 h-72 w-72 rounded-full bg-brand-pink/15 blur-3xl" />
          <div className="relative max-w-2xl">
            <Globe className="h-8 w-8 text-brand-pink mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight">
              15+ lingue. Zero compromessi sulla qualità.
            </h2>
            <p className="mt-4 text-muted-foreground">
              Che tu debba tradurre un'email, un contratto o una campagna di marketing, Freed Words
              adatta ogni parola al contesto e alla cultura di destinazione.
            </p>
            <Button data-testid="cta-bottom" onClick={() => nav(user ? "/app" : "/register")}
              className="mt-8 rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white h-11 px-7 gap-2">
              Prova ora <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row justify-between gap-2">
          <span>© 2026 Freed Words</span>
          <span>Traduzione premium a livello madrelingua.</span>
        </div>
      </footer>
    </div>
  );
}
